/**
 * Shared "does this look like a URL or filesystem path" regex heuristic.
 *
 * Originally lived only in `terminal-links.ts` (linkifying terminal output);
 * `ChatMarkdown.tsx` reuses the same patterns to detect file-path candidates
 * in chat message text, so the heuristic lives here instead of being
 * duplicated between the two.
 */

import { isPreviewablePath } from "@taskin-code/shared/filePreviewGlobs";

export type LinkMatchKind = "url" | "path";

export interface LinkMatch {
  readonly kind: LinkMatchKind;
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

export const URL_PATTERN = /https?:\/\/[^\s"'`<>]+/g;
export const FILE_PATH_PATTERN =
  /(?:~\/|\.{1,2}\/|\/|[A-Za-z]:\\|\\\\)[^\s"'`<>]+|[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)+(?::\d+){0,2}/g;
const TRAILING_PUNCTUATION_PATTERN = /[.,;!?]+$/;

export function trimClosingDelimiters(value: string): string {
  let output = value.replace(TRAILING_PUNCTUATION_PATTERN, "");
  if (output.length === 0) return output;

  const trimUnbalanced = (open: string, close: string) => {
    while (output.endsWith(close)) {
      const opens = output.split(open).length - 1;
      const closes = output.split(close).length - 1;
      if (opens >= closes) return;
      output = output.slice(0, -1);
    }
  };

  trimUnbalanced("(", ")");
  trimUnbalanced("[", "]");
  trimUnbalanced("{", "}");
  return output;
}

export function overlaps(
  a: { readonly start: number; readonly end: number },
  b: { readonly start: number; readonly end: number },
): boolean {
  return a.start < b.end && b.start < a.end;
}

export function collectMatches(
  line: string,
  kind: LinkMatchKind,
  pattern: RegExp,
  existing: ReadonlyArray<LinkMatch>,
): LinkMatch[] {
  const matches: LinkMatch[] = [];
  pattern.lastIndex = 0;

  for (const rawMatch of line.matchAll(pattern)) {
    const raw = rawMatch[0];
    const start = rawMatch.index ?? -1;
    if (start < 0 || raw.length === 0) continue;

    const trimmed = trimClosingDelimiters(raw);
    if (trimmed.length === 0) continue;
    if (kind === "path" && /^https?:\/\//i.test(trimmed)) continue;

    const candidate: LinkMatch = {
      kind,
      text: trimmed,
      start,
      end: start + trimmed.length,
    };

    const collides = [...existing, ...matches].some((other) => overlaps(candidate, other));
    if (collides) continue;

    matches.push(candidate);
  }

  return matches;
}

export interface PathCandidateMatch {
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

/**
 * File-path-looking substrings in free text, excluding anything that
 * overlaps a URL (already linkified by markdown's own autolink handling).
 * Sorted by position, matching how the tokens actually appear in the text.
 *
 * Deliberately loose — `FILE_PATH_PATTERN` matches anything slash-separated,
 * so `10/12` and `and/or` land here too. That is fine for the terminal, where
 * a wrong guess costs a link that opens nothing, but callers offering these
 * as previews must narrow the result first; see `extractPreviewablePaths`.
 */
export function extractPathCandidates(text: string): PathCandidateMatch[] {
  const urlMatches = collectMatches(text, "url", URL_PATTERN, []);
  const pathMatches = collectMatches(text, "path", FILE_PATH_PATTERN, urlMatches);
  return pathMatches
    .map(({ text: matchText, start, end }) => ({ text: matchText, start, end }))
    .toSorted((a, b) => a.start - b.start);
}

/**
 * The subset of path candidates that a preview request could actually serve:
 * absolute paths matching one of the project's configured globs.
 *
 * The allowlist is what separates a real file reference from a token that
 * merely looks like one — without it, a message saying "10/12" or "and/or"
 * would offer a link that can only ever fail. With nothing configured this
 * returns nothing at all, so an install that never set up file preview shows
 * no links in chat.
 */
export function extractPreviewablePaths(
  text: string,
  allowedGlobs: readonly string[],
): PathCandidateMatch[] {
  if (allowedGlobs.length === 0) return [];
  return extractPathCandidates(text).filter((candidate) =>
    isPreviewablePath(candidate.text, allowedGlobs),
  );
}
